import React, { useState, useEffect } from 'react';
import { KTSVG, SimpleAlert } from '../../../../../_mexablock/helpers';
import { Modal } from 'react-bootstrap-v5';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import RadioButtons from '../../../../../_mexablock/helpers/components/form-components/RadioButtons';
import { updateZona, getZonaById } from '../../../../service/catalog/zonas';
import clsx from 'clsx';

type Props = {
  zonaId: number;
  show: boolean;
  handleClose: (updateGrid: boolean) => void;
};

const ZonaSchema = Yup.object().shape({
  name: Yup.string().max(30, 'Máximo 30 caracteres').required('Requerido.'),
  first_name: Yup.string()
    .max(50, 'Máximo 50 caracteres')
    .when('hasResponsible', {
      is: true,
      then: (schema) => schema.required('Requerido.'),
      otherwise: (schema) => schema.notRequired(),
    }),
  mother_last_name: Yup.string()
    .max(50, 'Máximo 50 caracteres')
    .when('hasResponsible', {
      is: true,
      then: (schema) => schema.required('Requerido.'),
      otherwise: (schema) => schema.notRequired(),
    }),
  father_last_name: Yup.string()
    .max(50, 'Máximo 50 caracteres')
    .when('hasResponsible', {
      is: true,
      then: (schema) => schema.required('Requerido.'),
      otherwise: (schema) => schema.notRequired(),
    }),
  email: Yup.string()
    .email('Formato inválido.')
    .min(3, 'Mínimo 3 caracteres.')
    .max(50, 'Máximo 50 caracteres.')
    .required('Requerido.'),
  password: Yup.string()
    .matches(
      /^(?=.*[A-Z])(?=.*[@#$%^&\-+=()])(?=.*[0-9])(?=.*[a-z])(?=\S+$).{8,20}$/,
      'Formato de contraseña incorrecto'
    )
    .min(8, 'La contraseña debe tener al menos 8 caracteres.')
    .max(20, 'La contraseña no debe superar los 20 caracteres.'),

  confirmPassword: Yup.string().oneOf(
    [Yup.ref('password')],
    'La contraseña no coincide'
  ),
});

const defaultValues = {
  id: 0,
  name: '',
  email: '',
  hasResponsible: false,
  first_name: '',
  father_last_name: '',
  mother_last_name: '',
  password: '',
  confirmPassword: '',
};

const UpdateModal: React.FC<Props> = ({ show, handleClose, zonaId }) => {
  const [loading, setLoading] = useState(false);
  const [visivility, setVisivility] = useState(false);
  const [visivilityConfirm, setVisivilityConfirm] = useState(false);
  const [initValues, setInitValues] = useState(defaultValues);

  const responsibleOptions = [
    { id: 1, value: true, label: 'Sí' },
    { id: 2, value: false, label: 'No' },
  ];

  const formZona = useFormik({
    initialValues: initValues,
    validationSchema: ZonaSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        setLoading(true);
        const payload = { ...values };
        if (!payload.hasResponsible) {
          payload.first_name = '';
          payload.father_last_name = '';
          payload.mother_last_name = '';
        }

        const res = await updateZona(payload);
        SimpleAlert(res.data.message, 3000, 'success');
        resetForm();
        handleClose(true);
      } catch (err: any) {
        const { message, details } = err.response.data;
        SimpleAlert(details?.[0]?.message || message, 3000, 'error');
      } finally {
        setLoading(false);
        setSubmitting(false);
      }
    },
  });

  const handleChangeInput = (
    event: React.ChangeEvent<HTMLInputElement>,
    key: string
  ) => {
    const value = event.target.value;
    const valueReg = value.replace(/\d/g, '');
    const cleanInput = valueReg.replace(/^\s+|\s+(?=\s)/g, '');
    const inputLimpio = cleanInput.replace(/[^\wáéíóúüÁÉÍÓÚÜñÑ\s]/g, '');
    formZona.setFieldValue(key, inputLimpio);
  };

  const fetchPost = async () => {
    try {
      const zoneInit = await getZonaById(zonaId);

      if (zoneInit) {
        setInitValues({ ...defaultValues, ...zoneInit });
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (zonaId > 0) {
      fetchPost();
    }
  }, [show]);
  return (
    <Modal
      id="kt_modal_create"
      tabIndex={-1}
      aria-hidden="true"
      dialogClassName="modal-dialog modal-dialog-centered modal-xl"
      show={show}
      onHide={() => {
        formZona.resetForm();
        setVisivility(false);
        setVisivilityConfirm(false);
        handleClose(false);
      }}
    >
      <div className="modal-content">
        <div className="modal-header pb-0 border-0 justify-content-end">
          <div
            className="btn btn-sm btn-icon btn-active-color-primary"
            data-bs-dismiss="modal"
            onClick={() => {
              formZona.resetForm();
              setVisivility(false);
              setVisivilityConfirm(false);
              handleClose(false);
            }}
          >
            <KTSVG
              className="svg-icon-2"
              path="/media/icons/duotune/arrows/arr061.svg"
            />
          </div>
        </div>

        <div className="modal-body scroll-y mx-5 pt-0">
          <div className="text-center mb-13">
            <h1 className="mb-3">Agregar registro de zona</h1>
            <div className="text-muted fw-semibold fs-5">
              Incorpora nuevas zonas al catálogo ingresando la información
              correspondiente.
            </div>
          </div>
          <form
            id="kt_modal_generic_form"
            className="mx-auto w-100 mw-900px pt-10 pb-10"
            action="#"
            onSubmit={formZona.handleSubmit}
          >
            {/* ----------------- Zona ---------------- */}
            <div className="row g-9 mb-5">
              <div className="d-flex flex-stack">
                <h3 className="text-gray-500">Detalles del registro</h3>
              </div>
              <div className="separator separator-dashed mb-5" />
              <div className="col-md-4">
                <RadioButtons
                  title="¿Agregar responsable?"
                  options={responsibleOptions}
                  name="hasResponsible"
                  formData={formZona}
                  onChange={(value: boolean) => {
                    formZona.setFieldValue('hasResponsible', value);

                    // 🔹 Si selecciona "No", limpiamos los campos de responsable y contraseña
                    if (!value) {
                      formZona.setFieldValue('first_name', '');
                      formZona.setFieldValue('father_last_name', '');
                      formZona.setFieldValue('mother_last_name', '');
                      formZona.setFieldValue('password', '');
                      formZona.setFieldValue('confirmPassword', '');
                    }
                  }}
                />
              </div>
              <div className="col-md-4">
                <label className="required fw-bold fs-6 mb-2">
                  Nombre de zona
                </label>
                <input
                  placeholder="Nombre de zona"
                  {...formZona.getFieldProps('name')}
                  className="form-control mb-3 mb-lg-0"
                  type="text"
                  autoComplete="off"
                />
                {formZona.touched.name && formZona.errors.name && (
                  <div className="text-danger">{formZona.errors.name}</div>
                )}
              </div>
              <div className="col-md-4">
                <label className="required fw-bold fs-6 mb-2">Correo</label>
                <input
                  placeholder="Correo"
                  {...formZona.getFieldProps('email')}
                  className="form-control mb-3 mb-lg-0"
                  type="text"
                  autoComplete="off"
                />
                {formZona.touched.email && formZona.errors.email && (
                  <div className="text-danger">{formZona.errors.email}</div>
                )}
              </div>
            </div>
            {/* ----------------- Contraseñas ---------------- */}
            <div className="row g-9 mb-5">
              <div className="d-flex flex-stack">
                <h3 className="text-gray-500">Datos de acceso</h3>
              </div>
              <div className="separator separator-dashed mb-5" />
              <div className="col-md-6">
                <label className="fw-bold fs-6 mb-2">Contraseña</label>
                <div className={clsx('position-relative')}>
                  <input
                    type={visivility ? 'text' : 'password'}
                    maxLength={20}
                    autoComplete="off"
                    {...formZona.getFieldProps('password')}
                    className="form-control form-control-lg"
                  />
                  <span
                    className="btn btn-sm btn-icon position-absolute translate-middle top-50 end-0 me-n2"
                    onClick={() => setVisivility(!visivility)}
                  >
                    {visivility ? (
                      <i className="bi bi-eye fs-2"></i>
                    ) : (
                      <i className="bi bi-eye-slash fs-2"></i>
                    )}
                  </span>
                </div>
                {formZona.touched.password && formZona.errors.password && (
                  <div className="text-danger">{formZona.errors.password}</div>
                )}
              </div>

              <div className="col-md-6">
                <label className="fw-bold fs-6 mb-2">
                  Confirmar contraseña
                </label>
                <div className={clsx('position-relative')}>
                  <input
                    type={visivilityConfirm ? 'text' : 'password'}
                    maxLength={20}
                    autoComplete="off"
                    {...formZona.getFieldProps('confirmPassword')}
                    className="form-control form-control-lg"
                  />
                  <span
                    className="btn btn-sm btn-icon position-absolute translate-middle top-50 end-0 me-n2"
                    onClick={() => setVisivilityConfirm(!visivilityConfirm)}
                  >
                    {visivilityConfirm ? (
                      <i className="bi bi-eye fs-2"></i>
                    ) : (
                      <i className="bi bi-eye-slash fs-2"></i>
                    )}
                  </span>
                </div>
                {formZona.touched.confirmPassword &&
                  formZona.errors.confirmPassword && (
                    <div className="text-danger">
                      {formZona.errors.confirmPassword}
                    </div>
                  )}
              </div>
              <div className="form-text mb-5">
                La contraseña debe tener entre 8 y 20 caracteres, incluir
                mayúsculas, minúsculas, números y al menos uno de estos signos:
                @ # $ % ^ & - + = ( )
              </div>
            </div>
            {/* ----------------- Responsable ---------------- */}
            {formZona.values.hasResponsible && (
              <>
                <div className="row g-9 mb-5">
                  <div className="d-flex flex-stack">
                    <h3 className="text-gray-500">Responsable</h3>
                  </div>
                  <div className="separator separator-dashed mb-5" />

                  <div className="col-md-4">
                    <label className="required fw-bold fs-6 mb-2">Nombre</label>
                    <input
                      placeholder="Nombre"
                      {...formZona.getFieldProps('first_name')}
                      className="form-control mb-3 mb-lg-0"
                      type="text"
                      autoComplete="off"
                      onChange={(e) => handleChangeInput(e, 'first_name')}
                    />
                    {formZona.touched.first_name &&
                      formZona.errors.first_name && (
                        <div className="text-danger">
                          {formZona.errors.first_name}
                        </div>
                      )}
                  </div>

                  <div className="col-md-4">
                    <label className="required fw-bold fs-6 mb-2">
                      Apellido Paterno
                    </label>
                    <input
                      placeholder="Apellido Paterno"
                      {...formZona.getFieldProps('father_last_name')}
                      className="form-control mb-3 mb-lg-0"
                      type="text"
                      autoComplete="off"
                      onChange={(e) => handleChangeInput(e, 'father_last_name')}
                    />
                    {formZona.touched.father_last_name &&
                      formZona.errors.father_last_name && (
                        <div className="text-danger">
                          {formZona.errors.father_last_name}
                        </div>
                      )}
                  </div>

                  <div className="col-md-4">
                    <label className="required fw-bold fs-6 mb-2">
                      Apellido Materno
                    </label>
                    <input
                      placeholder="Apellido Materno"
                      {...formZona.getFieldProps('mother_last_name')}
                      className="form-control mb-3 mb-lg-0"
                      type="text"
                      autoComplete="off"
                      onChange={(e) => handleChangeInput(e, 'mother_last_name')}
                    />
                    {formZona.touched.mother_last_name &&
                      formZona.errors.mother_last_name && (
                        <div className="text-danger">
                          {formZona.errors.mother_last_name}
                        </div>
                      )}
                  </div>
                </div>
              </>
            )}

            {/* ----------------- Botones ---------------- */}
            <div className="text-center pt-10">
              <button
                type="reset"
                className="btn btn-light me-3"
                onClick={() => {
                  formZona.resetForm();
                  handleClose(false);
                }}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                {!loading && <span className="indicator-label">Guardar</span>}
                {loading && (
                  <span
                    className="indicator-progress"
                    style={{ display: 'block' }}
                  >
                    Espere por favor...
                    <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                  </span>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
};

export { UpdateModal };
